import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import { Stagger, StaggerItem } from "@/components/motion";
import CTABanner from "@/components/CTABanner";
import api from "@/lib/api";

const COVERS = ["/generated/seo.png", "/generated/branding.png", "/generated/growth.png", "/generated/cta.png"];

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEO
        title="Blog — Digital Marketing Insights"
        description="Actionable insights on Local SEO, web design, Google Ads, Meta Ads and digital marketing for local businesses in Bengaluru — from the DiziTeQ team."
        path="/blog"
      />
      <PageHeader
        eyebrow="Blog"
        title="Insights to help you"
        highlight="grow faster"
        subtitle="Practical, no-fluff guides on SEO, web design, advertising and growth for local businesses."
      />

      <section className="dq-container py-14" data-testid="blog-list">
        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-80 animate-pulse rounded-3xl bg-white" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <p className="text-dq-muted">No articles yet. Check back soon.</p>
        ) : (
          <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((p, i) => (
              <StaggerItem key={p.slug}>
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${i}`}
                  className="group flex h-full flex-col overflow-hidden rounded-3xl border border-dq-line bg-white transition-[transform,box-shadow] duration-300 hover:-translate-y-2 hover:dq-shadow-soft">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={p.cover_image || COVERS[i % COVERS.length]} alt={p.title} loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-dq-orange backdrop-blur">
                      {p.category}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-lg font-bold leading-snug text-dq-ink group-hover:text-dq-orange">{p.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-dq-muted">{p.excerpt}</p>
                    <div className="mt-5 flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5 text-dq-muted"><Clock className="h-4 w-4" /> {p.read_time}</span>
                      <span className="flex items-center gap-1 font-semibold text-dq-ink">Read <ArrowUpRight className="h-4 w-4" /></span>
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        )}
      </section>
      <div className="py-10"><CTABanner /></div>
    </>
  );
}
